/**
 * 09 §1 broadcast-bracket HUD, screen-space only (world readouts live in the
 * renderer). Virtual px × `hud.scale`; text additionally × `uiScaleText`.
 * Unaffected by hitstop — the caller passes the wall clock.
 */
import { OVERTIME_S, ROUND_TIME_S, SHIELD_MIN_RAISE, SIM_HZ, STAMINA_MAX, VIRTUAL_H, VIRTUAL_W } from '../../../sim/constants.js'
import { powerUpById } from '../../../sim/powerups.js'
import type { MatchPhase, PlayerState, RoundResult, SimState, Team } from '../../../sim/types.js'
import type { SimEvent } from '../../../shared/events.js'
import type { DisclordView, QueueStateMsg } from '../../../shared/protocol.js'
import type { Options } from '../options.js'
import { AMBER } from '../fx/constants.js'
import { drawGlyph, glyphForPowerUp } from '../glyphs.js'
import { CalloutQueue, fromSimEvent, type CalloutCtx } from './callouts.js'
import { KillFeed } from './killfeed.js'
import { bracket, callout, feedRow, hudFont, inkAlpha, monoFont, pip, scoreCell, segBar, setHudStyle, tag, teamAccent, teamGlyph, teamLetter, text, timer, type PipKind, type ScoreCellState } from './components.js'

export type ZoneId = 'Z1' | 'Z1b' | 'Z2' | 'Z3' | 'Z4' | 'Z5' | 'Z6' | 'Z7' | 'Z8' | 'Z9' | 'Z10' | 'Z11' | 'Z12'
export interface Rect { x: number; y: number; w: number; h: number }

/** 09 §1 anchor table at scale 1 (box origin top-left, in virtual px). */
const ZONES: Record<ZoneId, { anchor: 'tl' | 'tr' | 'bl' | 'br' | 'tc' | 'bc' | 'c' | 'full'; x: number; y: number; w: number; h: number }> = {
  Z1: { anchor: 'bl', x: 24, y: 696, w: 260, h: 56 },
  Z1b: { anchor: 'bl', x: 24, y: 632, w: 260, h: 28 },
  Z2: { anchor: 'tc', x: 640, y: 24, w: 160, h: 48 },
  Z3: { anchor: 'tc', x: 640, y: 76, w: 240, h: 22 },
  Z4: { anchor: 'tl', x: 24, y: 24, w: 160, h: 64 },
  Z5: { anchor: 'bl', x: 300, y: 704, w: 96, h: 24 },
  Z6: { anchor: 'tr', x: 1256, y: 24, w: 360, h: 88 },
  Z7: { anchor: 'c', x: 640, y: 300, w: 720, h: 96 },
  Z8: { anchor: 'br', x: 1256, y: 704, w: 80, h: 16 },
  Z9: { anchor: 'bc', x: 640, y: 700, w: 520, h: 32 },
  Z10: { anchor: 'c', x: 640, y: 560, w: 400, h: 40 },
  Z11: { anchor: 'full', x: 0, y: 0, w: VIRTUAL_W, h: VIRTUAL_H },
  Z12: { anchor: 'tl', x: 24, y: 24, w: 200, h: 40 },
}
export const SAFE_MARGIN = 24

/** 09 §6: visible safe area in virtual px. `fit` = the full 1280×720 frame;
 *  `fill` crops the long axis, so edge-anchored zones re-anchor inward. */
export interface SafeArea { x0: number; y0: number; x1: number; y1: number }
let SAFE: SafeArea = { x0: 0, y0: 0, x1: VIRTUAL_W, y1: VIRTUAL_H }
export function setSafeArea(a: SafeArea): void { SAFE = { x0: Math.max(0, a.x0), y0: Math.max(0, a.y0), x1: Math.min(VIRTUAL_W, a.x1), y1: Math.min(VIRTUAL_H, a.y1) } }
export function safeArea(): SafeArea { return SAFE }

/** Zone box at a HUD scale; anchors stay pinned to their screen edge so
 *  zones grow inward (09 §6 re-anchoring). */
export function zoneRect(zone: ZoneId, scale = 1): Rect {
  const z = ZONES[zone]
  const w = z.w * scale, h = z.h * scale
  // Edge offsets from the visible safe area (0 under `fit`).
  const L = SAFE.x0, R = SAFE.x1 - VIRTUAL_W, T = SAFE.y0, B = SAFE.y1 - VIRTUAL_H
  const CX = (SAFE.x0 + SAFE.x1) / 2 - VIRTUAL_W / 2, CY = (SAFE.y0 + SAFE.y1) / 2 - VIRTUAL_H / 2
  switch (z.anchor) {
    case 'tl': return { x: z.x + L, y: z.y + T, w, h }
    case 'tr': return { x: z.x + R - w, y: z.y + T, w, h }
    case 'bl': return { x: z.x + L, y: z.y + B - h, w, h }
    case 'br': return { x: z.x + R - w, y: z.y + B - h, w, h }
    case 'tc': return { x: z.x + CX - w / 2, y: z.y + T, w, h }
    case 'bc': return { x: z.x + CX - w / 2, y: z.y + B - h, w, h }
    case 'c': return { x: z.x + CX - w / 2, y: z.y + CY - h / 2, w, h }
    case 'full': return { x: SAFE.x0, y: SAFE.y0, w: SAFE.x1 - SAFE.x0, h: SAFE.y1 - SAFE.y0 }
  }
}

/** Z2 text per 09 §1: M:SS; tenths ≤ 10 s LIVE; OVERTIME counts up; PAUSED. */
export function formatTimer(m: { phase: MatchPhase; timerTicks: number; overtimeTicks: number; paused: boolean }, mode: 'hub' | 'match'): { text: string; label: string | null; last10: boolean } {
  if (mode === 'hub') return { text: '', label: null, last10: false }
  if (m.paused) return { text: 'PAUSED', label: null, last10: false }
  if (m.phase === 'OVERTIME') return { text: fmt(m.overtimeTicks / SIM_HZ, false), label: 'OVERTIME', last10: m.overtimeTicks / SIM_HZ >= OVERTIME_S - 10 }
  if (m.phase === 'LIVE') { const s = m.timerTicks / SIM_HZ; return { text: fmt(s, s <= 10), label: null, last10: s <= 10 } }
  if (m.phase === 'COUNTDOWN' || m.phase === 'RESET') return { text: fmt(ROUND_TIME_S, false), label: null, last10: false }
  return { text: fmt(m.timerTicks / SIM_HZ, false), label: m.phase === 'ROUND_END' || m.phase === 'BETWEEN' ? null : m.phase.replace('_', ' '), last10: false }
}
function fmt(sec: number, tenths: boolean): string {
  const s = Math.max(0, sec)
  const mm = Math.floor(s / 60), ss = Math.floor(s % 60)
  return tenths ? `${mm}:${String(ss).padStart(2, '0')}.${Math.floor((s % 1) * 10)}` : `${mm}:${String(ss).padStart(2, '0')}`
}

/** Z1 bar: filled segment count + hatched fraction (below SHIELD_MIN_RAISE). */
export function staminaSegments(stamina: number, max: number = STAMINA_MAX, segments = 10): { filled: number; hatch01: number; value01: number } {
  const value01 = Math.max(0, Math.min(1, stamina / max))
  return { filled: Math.floor(value01 * segments + 1e-6), hatch01: SHIELD_MIN_RAISE / max, value01 }
}

/** Z3 cells in roundNumber order + one empty cell per unplayed best-of round. */
export function scoreCells(results: RoundResult[], current: number, roundsToWin = 2): { state: ScoreCellState; team: Team | null; roundNumber: number }[] {
  const cells = results.map((r) => ({ state: (r.void ? 'void' : 'won') as ScoreCellState, team: r.winner, roundNumber: r.roundNumber }))
  const decided = results.filter((r) => !r.void).length
  const maxRounds = roundsToWin * 2 - 1
  for (let i = decided; i < maxRounds; i++) cells.push({ state: i === decided ? 'current' : 'empty', team: null, roundNumber: current + (i - decided) })
  return cells
}

export function ellipsise(name: string, max = 12): string {
  if (name.length <= max) return name
  const keep = max - 1
  const head = Math.ceil(keep / 2), tail = Math.floor(keep / 2)
  return `${name.slice(0, head)}…${name.slice(name.length - tail)}`
}

export type DiscPipState = PipKind

export interface HudFrame {
  state: SimState
  me: PlayerState | null
  localTeam: Team | null
  spectator: boolean
  eliminated: boolean
  mode: 'hub' | 'match'
  ping: { rtt: number; resyncFlashUntil: number }
  queue: QueueStateMsg | null
  names: (id: string) => string
  teammate: PlayerState | null
  discStates: DiscPipState[]
  powerUp: string | null
  hubLoadout: string[]
  /** 12 §1 disclord (Sprint 8): hub-only level line under Z12. */
  disclord?: DisclordView | null
  pause: { name: string; leftMs: number } | null
  resume: number | null
  reconnecting?: boolean
  fps?: number
  device: 'mouse' | 'pad'
  options: Options
  trainingStep?: { n: number; text: string; hint?: string | null; total: number; skipPending?: boolean }
  prompt?: { text: string } | null
}

export class BroadcastHud {
  readonly callouts = new CalloutQueue()
  readonly feed = new KillFeed()
  private firstSeen = new Map<ZoneId, number>()
  private decidedRounds = 0
  private lastTimerSecond = -1
  private tickPulseAt = -Infinity

  /** Feed kill feed + call-outs from one snapshot's events. */
  ingest(events: SimEvent[], now: number, ctx: { localTeam: Team | null; spectator: boolean; hub: boolean; localId: string; names: (id: string) => string; teamOf: (id: string) => Team }): void {
    for (const e of events) {
      if (e.type === 'roundEnd' && !e.void) this.decidedRounds++
      if (e.type === 'phase' && e.phase === 'LOBBY_PICK') this.decidedRounds = 0
      if (e.type === 'matchEnd') this.decidedRounds = 0
    }
    const cctx: CalloutCtx = { localTeam: ctx.localTeam, spectator: ctx.spectator, now, decidedRounds: this.decidedRounds, hub: ctx.hub, localId: ctx.localId }
    for (const e of events) for (const c of fromSimEvent(e, cctx)) this.callouts.push(c)
    this.feed.ingest(events, now, ctx.names, ctx.teamOf, { hub: ctx.hub })
  }

  private appear(zone: ZoneId, now: number, reduced: boolean): number {
    let t = this.firstSeen.get(zone)
    if (t === undefined) { t = now; this.firstSeen.set(zone, now) }
    return reduced ? 1 : Math.min(1, (now - t) / 120)
  }
  private gone(zone: ZoneId): void { this.firstSeen.delete(zone) }

  draw(ctx: CanvasRenderingContext2D, f: HudFrame, now: number): void {
    const o = f.options
    const sc = o['hud.scale']
    const ts = o['a11y.uiScaleText']
    const reduced = o['a11y.reducedMotion']
    setHudStyle({ highContrast: o['a11y.highContrast'], scale: sc })
    ctx.save()
    ctx.textBaseline = 'alphabetic'
    const m = f.state.match
    const playing = !f.spectator && f.me !== null
    const inMatch = f.mode === 'match'

    // Z11 pause overlay (dim 40 %, no blur).
    if (f.pause || f.reconnecting) { ctx.fillStyle = 'rgba(11,13,16,0.4)'; ctx.fillRect(0, 0, VIRTUAL_W, VIRTUAL_H) }

    // Z1 stamina / Z1b teammate / Z5 pips.
    if (playing && f.me && !f.eliminated) {
      const r = zoneRect('Z1', sc); const p = this.appear('Z1', now, reduced)
      bracket(ctx, r.x, r.y, r.w, r.h, { corners: ['tl', 'br'], progress: p })
      const seg = staminaSegments(f.me.stamina, f.me.tuning.STAMINA_MAX)
      const exhausted = f.me.state === 'EXHAUST'
      const flash = exhausted && Math.floor(now / (reduced ? 250 : 125)) % 2 === 0
      const barY = r.y + 30 * sc
      segBar(ctx, r.x + 20 * sc, barY, 220 * sc, 10 * sc, 10, seg.value01, { hatchBelow01: seg.hatch01, dim: f.me.regenDelayTicks > 0 && !exhausted, flashOutline: flash })
      text(ctx, exhausted ? 'EXHAUSTED' : 'STAMINA', r.x + 20 * sc, r.y + 20 * sc, { font: hudFont(12 * sc * ts, 700), ink: exhausted ? AMBER : undefined })
      text(ctx, String(Math.round(f.me.stamina)).padStart(2, ' '), r.x + r.w - 12 * sc, r.y + 20 * sc, { font: hudFont(14 * sc * ts, 700), align: 'right' })
      if (f.me.state === 'SHIELD') { ctx.beginPath(); ctx.arc(r.x + 10 * sc, r.y + 16 * sc, 5 * sc, -Math.PI * 0.8, -Math.PI * 0.2); ctx.strokeStyle = inkAlpha(0.85); ctx.lineWidth = 1.5; ctx.stroke() }
      const z5 = zoneRect('Z5', sc)
      f.discStates.forEach((k, i) => pip(ctx, k, z5.x + 7 * sc + i * 20 * sc, z5.y + 12 * sc, 14 * sc))
    } else { this.gone('Z1') }

    if (playing && f.teammate && o['hud.teammateMini']) {
      const promoted = f.eliminated
      const r = promoted ? zoneRect('Z1', sc) : zoneRect('Z1b', sc)
      const p = this.appear('Z1b', now, reduced)
      bracket(ctx, r.x, r.y, r.w, r.h, { corners: ['tl', 'br'], progress: p, arm: promoted ? 18 : 10 })
      const tm = f.teammate
      const fs = (promoted ? 16 : 14) * sc * ts
      let nx = r.x + 8 * sc
      const g = glyphForPowerUp(tm.powerUps[0])
      if (g !== 'empty') { drawGlyph(ctx, g, nx + 8 * sc, r.y + 12 * sc, 16 * sc); nx += 20 * sc }
      drawGlyph(ctx, teamGlyph(tm.team), nx + 6 * sc, r.y + 12 * sc, 10 * sc, teamAccent(tm.team)); nx += 16 * sc
      text(ctx, ellipsise(tm.name), nx, r.y + 17 * sc, { font: hudFont(fs), ink: teamAccent(tm.team) })
      if (tm.alive) {
        const seg = staminaSegments(tm.stamina, tm.tuning.STAMINA_MAX)
        segBar(ctx, r.x + 130 * sc, r.y + 10 * sc, 120 * sc, 6 * sc, 10, seg.value01, { hatchBelow01: seg.hatch01 })
        for (let i = 0; i < tm.maxDiscs; i++) pip(ctx, i < tm.discsHeld.length ? 'held' : 'empty', r.x + 134 * sc + i * 10 * sc, r.y + 22 * sc, 6 * sc)
      } else text(ctx, 'DEREZZED', r.x + 130 * sc, r.y + 17 * sc, { font: hudFont(12 * sc * ts, 700), alpha: 0.8 })
    } else this.gone('Z1b')

    // Z2 timer + Z3 score strip (match + spectate).
    if (inMatch) {
      const r = zoneRect('Z2', sc); const p = this.appear('Z2', now, reduced)
      const t = formatTimer(m, f.mode)
      const sec = Math.floor(m.phase === 'OVERTIME' ? m.overtimeTicks / SIM_HZ : m.timerTicks / SIM_HZ)
      if (t.last10 && sec !== this.lastTimerSecond) { this.lastTimerSecond = sec; this.tickPulseAt = now }
      const pulse = t.last10 && !reduced ? 1 + 0.08 * Math.max(0, 1 - (now - this.tickPulseAt) / 150) : 1
      bracket(ctx, r.x, r.y, r.w, r.h, { corners: ['tl', 'tr', 'bl', 'br'], progress: p, ticks: 3, ink: t.last10 ? AMBER : undefined })
      if (t.label) text(ctx, t.label, r.x + r.w / 2, r.y + 12 * sc, { font: hudFont(11 * sc * ts, 700), align: 'center', alpha: 0.85 })
      const size = (t.last10 ? 40 : 36) * sc * ts * pulse
      timer(ctx, t.text, r.x + r.w / 2, r.y + r.h - 8 * sc, size, { last10: t.last10 })

      const r3 = zoneRect('Z3', sc)
      const cells = scoreCells(m.roundResults, m.roundNumber)
      const cs = 10 * sc, pitch = 20 * sc
      const cx0 = r3.x + r3.w / 2 - ((cells.length - 1) * pitch) / 2
      cells.forEach((c, i) => scoreCell(ctx, c.state, c.team, cx0 + i * pitch, r3.y + r3.h / 2, cs))
      for (const tm of [0, 1] as Team[]) {
        const x = tm === 0 ? r3.x + 8 * sc : r3.x + r3.w - 8 * sc
        drawGlyph(ctx, teamGlyph(tm), x + (tm === 0 ? 6 : -6) * sc, r3.y + r3.h / 2, 10 * sc, teamAccent(tm))
        text(ctx, `${teamLetter(tm)} ${m.score[tm]}`, x + (tm === 0 ? 16 : -16) * sc, r3.y + r3.h / 2 + 5 * sc, { font: hudFont(14 * sc * ts, 700), ink: teamAccent(tm), align: tm === 0 ? 'left' : 'right' })
      }
      // Spectator glyph strip under Z3 (09 §6).
      if (f.spectator) {
        const y = r3.y + r3.h + 14 * sc
        let x = r3.x + 8 * sc
        for (const tm of [0, 1] as Team[]) {
          text(ctx, `${teamLetter(tm)}:`, x, y + 4 * sc, { font: hudFont(12 * sc * ts, 700), ink: teamAccent(tm) }); x += 16 * sc
          for (const pl of f.state.players.filter((q) => q.team === tm)) { drawGlyph(ctx, glyphForPowerUp(pl.powerUps[0]), x + 8 * sc, y, 14 * sc); x += 18 * sc }
          x += 12 * sc
        }
      }
    } else { this.gone('Z2'); this.lastTimerSecond = -1 }

    // Z4 locked power-up (own) — also hub loadout.
    const pu = f.powerUp ?? (f.mode === 'hub' ? f.hubLoadout[0] ?? null : null)
    if (playing && pu && (!f.mode || f.mode !== 'hub' || f.hubLoadout.length)) {
      const def = powerUpById(pu)
      if (def) {
        const r = zoneRect(f.mode === 'hub' ? 'Z12' : 'Z4', sc)
        const y0 = f.mode === 'hub' ? r.y + (f.disclord ? 66 : 48) * sc : r.y
        const p = this.appear('Z4', now, reduced)
        bracket(ctx, r.x, y0, 160 * sc, 64 * sc, { corners: ['tl', 'br'], progress: p })
        drawGlyph(ctx, def.glyph, r.x + 28 * sc, y0 + 32 * sc, 40 * sc)
        text(ctx, def.name.toUpperCase(), r.x + 56 * sc, y0 + 26 * sc, { font: hudFont(16 * sc * ts, 700) })
        text(ctx, `+${short(def.buff)} / −${short(def.nerf)}`, r.x + 56 * sc, y0 + 44 * sc, { font: monoFont(10 * sc * ts), alpha: 0.75 })
      }
    } else this.gone('Z4')

    // Z12 hub queue tag.
    if (f.mode === 'hub' && !f.spectator) {
      const r = zoneRect('Z12', sc); const p = this.appear('Z12', now, reduced)
      bracket(ctx, r.x, r.y, r.w, r.h + (f.disclord ? 18 * sc : 0), { corners: ['tl', 'br'], progress: p }) // taller with the level line
      const q = f.queue
      const on = q?.state === 'FLAGGED' || q?.state === 'PULLED'
      text(ctx, z12Text(q, on, f.device), r.x + 10 * sc, r.y + 26 * sc, { font: hudFont(16 * sc * ts, 700), ink: on ? AMBER : undefined })
      if (f.disclord) text(ctx, levelLine(f.disclord), r.x + 10 * sc, r.y + 46 * sc, { font: monoFont(11 * sc * ts), alpha: 0.75 })
    } else this.gone('Z12')

    // Z6 kill feed.
    if (o['hud.killFeed']) {
      const r = zoneRect('Z6', sc)
      const rows = this.feed.rows(now, { hub: f.mode === 'hub' })
      const rh = 22 * sc
      rows.forEach((row, i) => {
        const y = r.y + rh * (FEED_ROWS - rows.length + i) + 16 * sc
        ctx.save()
        if (row.progress < 1) { ctx.beginPath(); ctx.rect(r.x, y - rh, r.w * row.progress, rh); ctx.clip() }
        feedRow(ctx, row.data, r.x + r.w, y, { strike: row.strike, scale: sc * ts })
        ctx.restore()
      })
    }

    // Z7 call-out band.
    const cur = this.callouts.current(now, reduced)
    const r7 = zoneRect('Z7', sc)
    if (cur) callout(ctx, cur.text, r7.x + r7.w / 2, r7.y + r7.h / 2 + cur.size * sc * ts * 0.35, cur.size * sc * ts, { progress: cur.progress, sub: cur.sub, team: cur.team })
    if (f.trainingStep) {
      const y = r7.y + r7.h + 30 * sc
      callout(ctx, f.trainingStep.text, r7.x + r7.w / 2, y, 24 * sc * ts, { progress: 1 })
      const rail = Array.from({ length: f.trainingStep.total }, (_, i) => i + 1)
      rail.forEach((n, i) => {
        const x = r7.x + r7.w / 2 - ((rail.length - 1) * 22 * sc) / 2 + i * 22 * sc
        text(ctx, String(n), x, y + 28 * sc, { font: hudFont(14 * sc * ts, n < f.trainingStep!.n ? 700 : 500), align: 'center', alpha: n <= f.trainingStep!.n ? 1 : 0.4 })
        if (n === f.trainingStep!.n) bracket(ctx, x - 9 * sc, y + 14 * sc, 18 * sc, 18 * sc, { corners: ['tl', 'br'], arm: 5, weight: 1.5 })
      })
      if (f.trainingStep.hint) text(ctx, f.trainingStep.hint, r7.x + r7.w / 2, y + 50 * sc, { font: hudFont(16 * sc * ts), align: 'center', alpha: 0.8, ink: AMBER })
      text(ctx, f.trainingStep.skipPending ? 'SKIP? [ENTER]' : '[ESC] SKIP TRAINING', r7.x + r7.w / 2, y + 70 * sc, { font: hudFont(12 * sc * ts), align: 'center', alpha: 0.6 })
    }

    // Z8 ping (+ fps).
    if (o['hud.showPing'] || o['video.showFps']) {
      const r = zoneRect('Z8', sc)
      const parts: string[] = []
      if (o['hud.showPing']) {
        const ms = Math.round(f.ping.rtt)
        const warn = ms >= 200 ? '▲▲' : ms >= 120 ? '▲' : ''
        parts.push(now < f.ping.resyncFlashUntil ? 'SYNC' : `${ms} ms ${warn}`.trim())
      }
      if (o['video.showFps'] && f.fps !== undefined) parts.push(`${f.fps.toFixed(0)} fps`)
      const amber = f.ping.rtt >= 120 || now < f.ping.resyncFlashUntil
      text(ctx, parts.join(' · '), r.x + r.w, r.y + r.h, { font: monoFont(12 * sc * ts), align: 'right', ink: amber && o['hud.showPing'] ? AMBER : undefined })
    }

    // Z9 spectator bar.
    if (f.spectator) {
      const r = zoneRect('Z9', sc); const p = this.appear('Z9', now, reduced)
      bracket(ctx, r.x, r.y, r.w, r.h, { corners: ['bl', 'br'], progress: p })
      const names = (tm: Team) => f.state.players.filter((q) => q.team === tm).map((q) => ellipsise(q.name)).join(' + ') || '—'
      const fs = 14 * sc * ts
      ctx.font = hudFont(fs, 700)
      const a = names(0), b = names(1)
      const wa = ctx.measureText(a).width, wb = ctx.measureText(b).width, wv = ctx.measureText(' vs ').width, wl = ctx.measureText('SPECTATING · ').width
      let x = r.x + r.w / 2 - (wl + wa + wv + wb) / 2
      const y = r.y + 14 * sc
      text(ctx, 'SPECTATING · ', x, y, { font: hudFont(fs, 700) }); x += wl
      for (const [tm, nm, w] of [[0, a, wa], [1, b, wb]] as [Team, string, number][]) {
        if (f.localTeam === tm) bracket(ctx, x - 4 * sc, y - fs, w + 8 * sc, fs + 6 * sc, { corners: ['tl', 'br'], arm: 5, weight: 1, ink: teamAccent(tm) })
        text(ctx, nm, x, y, { font: hudFont(fs, 700), ink: teamAccent(tm) }); x += w
        if (tm === 0) { text(ctx, ' vs ', x, y, { font: hudFont(fs, 700) }); x += wv }
      }
      text(ctx, `${f.device === 'pad' ? '[Y]' : '[Q]'} SWITCH SIDE · ${f.device === 'pad' ? '[B]' : '[ESC]'} LEAVE`, r.x + r.w / 2, r.y + r.h - 4 * sc, { font: hudFont(11 * sc * ts), align: 'center', alpha: 0.7 })
    } else this.gone('Z9')

    // Z10 state banner + prompt mini bracket.
    let banner: string | null = null
    if (f.reconnecting) banner = 'RECONNECTING…'
    else if (f.pause) banner = `WAITING FOR ${ellipsise(f.pause.name)} — ${fmt(f.pause.leftMs / 1000, false)}`
    else if (f.resume !== null) banner = `RESUMING ${f.resume}`
    else if (f.eliminated && playing && inMatch && f.me) banner = `DEREZZED — SPECTATING TEAM ${teamLetter(f.me.team)}`
    if (banner) {
      const r = zoneRect('Z10', sc); const p = this.appear('Z10', now, reduced)
      bracket(ctx, r.x, r.y, r.w, r.h, { corners: ['tl', 'br'], progress: p })
      text(ctx, banner, r.x + r.w / 2, r.y + r.h / 2 + 7 * sc, { font: hudFont(20 * sc * ts, 700), align: 'center' })
    } else this.gone('Z10')
    if (f.prompt) {
      const r = zoneRect('Z10', sc)
      const y = r.y + r.h + 24 * sc
      ctx.font = hudFont(16 * sc * ts)
      const w = ctx.measureText(f.prompt.text).width + 20 * sc
      bracket(ctx, r.x + r.w / 2 - w / 2, y - 18 * sc, w, 26 * sc, { corners: ['tl', 'br'], arm: 6, weight: 1 })
      text(ctx, f.prompt.text, r.x + r.w / 2, y, { font: hudFont(16 * sc * ts), align: 'center', alpha: 0.9 })
    }
    ctx.restore()
  }
}

const FEED_ROWS = 4

function short(o: Record<string, unknown>): string {
  return Object.entries(o).map(([k, v]) => `${k.replace(/_/g, ' ').toLowerCase().replace(/ ticks| base/g, '')} ${v}`).join(', ')
}

export { tag }

/** Z12 tag (10 §2, D11): `[TAB] QUEUE: ON · n/4`, then one trailer — the fill countdown, the vs-BOT countdown while
 *  flagged alone, or the `[B] vs BOT` offer once it opens. */
export function z12Text(q: QueueStateMsg | null, on: boolean, device: 'mouse' | 'pad'): string {
  const mmss = (ms: number): string => { const s = Math.ceil(ms / 1000); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` }
  let trailer = ''
  if (on && q) {
    if (q.waitingForParty) trailer = ' · WAITING FOR PARTY'
    else if (q.launchInMs != null) trailer = ` · launching in ${mmss(q.launchInMs)}`
    else if (q.vsBotAvailable) trailer = ' · [B] vs BOT'
    else if (q.vsBotInMs != null) trailer = ` · vs BOT in ${mmss(q.vsBotInMs)}`
  }
  return `${device === 'pad' ? '[START]' : '[TAB]'} QUEUE: ${on ? 'ON' : 'OFF'} · ${q?.flagged ?? 0}/4${trailer}`
}

/** Hub level line (12 §1): `LVL 7 · 120 / 1240 XP · 2 SLOTS`; at MAX_LEVEL the XP fraction is dropped. */
export function levelLine(d: DisclordView): string {
  const xp = d.toNext > 0 ? ` · ${d.into} / ${d.toNext} XP` : ' · MAX'
  return `LVL ${d.level}${xp} · ${d.slotsOpen} SLOT${d.slotsOpen === 1 ? '' : 'S'}`
}
